import React, { FunctionComponent } from 'react'
import { T } from '@transifex/react'
import VendNumber, { round } from 'vend-number'
import { Text } from '@lightspeed/design-system-react'
import { Currency } from '@vend/business-components'
import { CustomerBalances as CustomerBalancesModel } from '../../../models/sale'
import { useRetailerSettingsManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCustomerBalances,
  selectSaleCustomer,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { customerBalancesManager } from '../../../managers/customer-balances-manager'
import { isValidCustomer } from '../../../functions/sale'
import {
  FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER,
  FEATURE_CUSTOMER_BALANCE_SPLIT,
} from '../../../constants'
import { getFeatureFromCache } from '../../../util/feature-cache'
import {
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../../../functions/customer'
import { useCustomerLoyaltyInfo } from './hooks/useCustomerLoyaltyInfo'

export const CustomerBalancesSection: FunctionComponent = () => {
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const validCustomer = isValidCustomer(saleCustomer)
  const customerBalances = useAppSelector(selectCustomerBalances)
  const retailerSettingsManager = useRetailerSettingsManager()
  const isStoreCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()
  const isLoyaltyEnabledAtRetailer = retailerSettingsManager.isLoyaltyEnabled()

  const {
    showThresholdInfo,
    customerLoyaltyBalance,
    calculateCustomerThresholdValue,
    getThresholdValue,
  } = useCustomerLoyaltyInfo() || {}

  const determineStoreCreditBalance = () => {
    const creditBalance = customerBalancesManager.calculateStoreCreditBalance()
    return round(creditBalance, 2, VendNumber.ROUNDING_MODES.ROUND_DOWN)
  }

  const shouldShowBalanceSplit =
    getFeatureFromCache(FEATURE_CUSTOMER_BALANCE_SPLIT) &&
    getFeatureFromCache(FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER)

  const getOnAccountLimitBalance = (balances: CustomerBalancesModel) => {
    return balances.availableOnAccountLimitBalance < 0
      ? 0
      : balances.availableOnAccountLimitBalance
  }

  const SubLabel = ({ children }: { children: React.ReactNode }) => (
    <Text type="supplementary" className="vd-pt1">
      {children}
    </Text>
  )

  // Uses saleCustomer.on_account_limit (not customerBalances.onAccountLimit which coalesces null → 0)
  const renderAccountBalanceSubLabel = (balances: CustomerBalancesModel) => {
    if (shouldShowBalanceSplit) {
      if (isOnAccountLimitDisabled(saleCustomer)) {
        return (
          <SubLabel>
            <T
              _str="Not enabled"
              _context="Customer balances account limit not enabled label"
            />
          </SubLabel>
        )
      }
      if (isOnAccountLimitUnlimited(saleCustomer)) {
        return (
          <SubLabel>
            <T
              _str="No limit"
              _context="Customer balances account no limit label"
            />
          </SubLabel>
        )
      }
      return (
        <SubLabel>
          <T
            _str="Available:"
            _context="Customer balances account limit available label"
          />
          &nbsp;
          <Currency value={getOnAccountLimitBalance(balances)} />
        </SubLabel>
      )
    }

    // FF off — legacy label
    return balances.onAccountLimit !== null ? (
      <SubLabel>
        <T
          _str="On account limit available:"
          _context="Customer balances account limit label"
        />
        &nbsp;
        <Currency value={getOnAccountLimitBalance(balances)} />
      </SubLabel>
    ) : null
  }

  return (
    <div className="wr-customer-badge-balances vd-mt0">
      {customerBalances && saleCustomer && validCustomer && (
        <ul className="wr-info-fields wr-info-fields--large wr-info-fields--center vd-mb3">
          <li className="wr-info-field">
            <div className="wr-info-field-label">
              <T
                _str="Account balance"
                _context="Customer balances account balance label"
              />
            </div>
            <div className="wr-info-field-value pro-customer-balances-amount--account">
              <Currency value={customerBalances.account} />
            </div>
            {renderAccountBalanceSubLabel(customerBalances)}
          </li>
          {shouldShowBalanceSplit && (
            <li className="wr-info-field">
              <div className="wr-info-field-label">
                <T
                  _str="Layaway balance"
                  _context="Customer balances layaway balance label"
                />
              </div>
              <div className="wr-info-field-value pro-customer-balances-amount--layby">
                <Currency value={customerBalances.laybyBalance ?? 0} />
              </div>
            </li>
          )}
          {isLoyaltyEnabledAtRetailer && (
            <li className="wr-info-field">
              <div className="wr-info-field-label">
                <T
                  _str="Loyalty balance"
                  _context="Customer balances loyalty label"
                />
              </div>
              {saleCustomer.enable_loyalty ? (
                <>
                  <div className="wr-info-field-value wr-customer-loyalty-balance">
                    <Currency value={customerLoyaltyBalance} />
                  </div>
                  {showThresholdInfo && (
                    <>
                      <div>
                        <Text type="supplementary" className="vd-pt1">
                          <T
                            _str="Redemption minimum: {minimalLoyaltyAmount},"
                            _context="Customer loyalty redemption minimum"
                            minimalLoyaltyAmount={
                              <Currency value={getThresholdValue()} />
                            }
                          />
                        </Text>
                      </div>
                      <div>
                        <div>
                          <Text type="supplementary" className="vd-pt1">
                            <T
                              _str="Need {amountToSpend} more"
                              _context="Amount for customer loyalty activation"
                              amountToSpend={
                                <Currency
                                  value={calculateCustomerThresholdValue()}
                                />
                              }
                            />
                          </Text>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : shouldShowBalanceSplit ? (
                <div className="wr-info-field-value wr-customer-loyalty-balance">
                  <Currency value={0} />
                </div>
              ) : (
                <div className="customer-card--loyalty-status">
                  <T
                    _str="Inactive"
                    _context="Customer balances disabled value"
                  />
                </div>
              )}
            </li>
          )}
          {isStoreCreditEnabled && (
            <li className="wr-info-field">
              <div className="wr-info-field-label">
                <T
                  _str="Store credit"
                  _context="Customer balances store credit label"
                />
              </div>
              <div className="wr-info-field-value">
                <div className="pro-customer-balances-amount--store-credit">
                  <Currency value={determineStoreCreditBalance()} />
                </div>
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
